import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { projects } from '../../../mocks/projects';
import LazyImage from '../../../components/base/LazyImage';

// Schema.org CreativeWork markup generator
function ProjectSchema({ project }: { project: typeof projects[0] }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": project.name,
    "description": `${project.fullDescription.problem} ${project.fullDescription.solution} ${project.fullDescription.result}`,
    "url": project.url,
    "image": project.image,
    "dateCreated": project.year,
    "creator": {
      "@type": "Person",
      "name": "Giedrius Petrauskas",
      "url": "https://giedriuspetrauskas.com"
    },
    "genre": project.category,
    "keywords": project.tags.join(', '),
    "provider": {
      "@type": "Person",
      "name": "Giedrius Petrauskas"
    }
  };

  useEffect(() => {
    const scriptId = `schema-project-${project.slug}`;
    let scriptEl = document.getElementById(scriptId);
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = scriptId;
      scriptEl.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(schema);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [project.slug]);

  return null;
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  const project = projects.find(p => p.slug === id);

  useEffect(() => {
    if (!project) {
      navigate('/portfolio');
      return;
    }
    window.scrollTo(0, 0);
    setTimeout(() => setIsLoaded(true), 100);
  }, [project, navigate]);

  if (!project) {
    return null;
  }

  const otherProjects = projects.filter(p => p.id !== project.id).slice(0, 2);

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org CreativeWork */}
      <ProjectSchema project={project} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <nav className={`mb-8 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-gray-500 hover:text-[#1B4332] transition-colors">
                  Home
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <Link to="/portfolio" className="text-gray-500 hover:text-[#1B4332] transition-colors">
                  Portfolio
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-[#1B4332] font-medium">{project.name}</li>
            </ol>
          </nav>

          {/* Header */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className={`space-y-6 transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-[#52796F] tracking-wider uppercase">{project.category}</span>
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-400">{project.year}</span>
              </div>

              <h1 className="font-serif text-5xl lg:text-6xl text-[#2D3436]">
                {project.name}
              </h1>

              {/* Structured description: Problem / Solution / Outcome */}
              <div className="space-y-4 pt-1">
                <div>
                  <span className="text-xs font-semibold tracking-widest uppercase text-[#52796F]">Problem</span>
                  <p className="mt-1 text-base text-gray-600 leading-relaxed">
                    {project.fullDescription.problem}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold tracking-widest uppercase text-[#52796F]">Solution</span>
                  <p className="mt-1 text-base text-gray-600 leading-relaxed">
                    {project.fullDescription.solution}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold tracking-widest uppercase text-[#52796F]">Result</span>
                  <p className="mt-1 text-base text-gray-600 leading-relaxed">
                    {project.fullDescription.result}
                  </p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 pt-2">
                {project.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <div className="pt-4">
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#1B4332] text-white px-8 py-4 rounded-full hover:bg-[#0f2419] transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group whitespace-nowrap"
                >
                  <span className="font-semibold">Visit Live Website</span>
                  <i className="ri-arrow-right-up-line text-xl group-hover:rotate-45 transition-transform duration-300"></i>
                </a>
              </div>
            </div>

            {/* Project Info Card */}
            <div className={`bg-white rounded-3xl p-8 shadow-xl border border-gray-100 transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h3 className="font-serif text-2xl text-[#2D3436] mb-6">Project Details</h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                  <span className="text-gray-500">Client</span>
                  <span className="font-medium text-[#2D3436]">{project.client}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                  <span className="text-gray-500">Duration</span>
                  <span className="font-medium text-[#2D3436]">{project.duration}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                  <span className="text-gray-500">Year</span>
                  <span className="font-medium text-[#2D3436]">{project.year}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                  <span className="text-gray-500">Category</span>
                  <span className="font-medium text-[#2D3436]">{project.category}</span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-3">Services</span>
                  <div className="flex flex-wrap gap-2">
                    {project.services.map((service) => (
                      <span 
                        key={service}
                        className="px-3 py-1 bg-[#1B4332]/10 text-[#1B4332] text-sm rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Image */}
      <section className={`py-16 transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <div className="relative w-full h-[600px]">
              <LazyImage 
                src={project.image} 
                alt={project.imageAlt || `${project.name} - ${project.category} project`}
                className="w-full h-full object-cover object-top"
                wrapperClassName="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Challenge */}
            <div className={`space-y-6 transition-all duration-700 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="w-16 h-16 bg-[#1B4332]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-question-line text-3xl text-[#1B4332]"></i>
              </div>
              <h2 className="font-serif text-3xl text-[#2D3436]">The Challenge</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {project.challenge}
              </p>
            </div>

            {/* Solution */}
            <div className={`space-y-6 transition-all duration-700 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="w-16 h-16 bg-[#1B4332]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-lightbulb-line text-3xl text-[#1B4332]"></i>
              </div>
              <h2 className="font-serif text-3xl text-[#2D3436]">The Solution</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {project.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20">
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          <div className={`text-center mb-12 transition-all duration-700 delay-600 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-serif text-4xl text-[#2D3436]">Key Results</h2>
          </div>

          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {project.results.map((result, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#1B4332] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-white"></i>
                </div>
                <p className="font-medium text-[#2D3436]">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-[#2D3436]">Other Projects</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {otherProjects.map((otherProject) => (
              <Link
                key={otherProject.id}
                to={`/portfolio/${otherProject.slug}`}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="relative w-full h-[300px] overflow-hidden">
                  <LazyImage 
                    src={otherProject.image} 
                    alt={otherProject.imageAlt || `${otherProject.name} project`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    wrapperClassName="w-full h-full"
                  />
                  <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-full shadow-lg">
                    <span className="text-sm font-medium text-[#2D3436]">{otherProject.category}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-[#2D3436] mb-2 group-hover:text-[#1B4332] transition-colors">
                    {otherProject.name}
                  </h3>
                  <p className="text-gray-600">{otherProject.shortDescription}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/portfolio"
              className="inline-flex items-center gap-2 text-[#1B4332] font-medium text-lg hover:gap-4 transition-all duration-300 group"
            >
              <span>View All Projects</span>
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform duration-300"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          <div className="bg-[#1B4332] rounded-3xl p-12 lg:p-16 text-center">
            <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6">
              Have a project in mind?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Share a few details about your project and I&apos;ll review it personally.&nbsp;If there&apos;s a strong fit, we can discuss the next steps.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-4 bg-white text-[#1B4332] px-10 py-5 rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group whitespace-nowrap"
            >
              <span className="font-semibold text-lg">Submit Project Inquiry</span>
              <div className="w-10 h-10 bg-[#1B4332] rounded-full flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
